import type { VerificationResult } from "../verify/result";

export type DownloadInfo = { proofUrl?: string; proofBase64?: string };

export type UiState =
  | { kind: "loading" }
  | { kind: "verify-result"; result: VerificationResult; download?: DownloadInfo }
  | { kind: "offline-empty" }
  | { kind: "offline-result"; result: VerificationResult; download?: DownloadInfo }
  | { kind: "stamp-not-found" }
  | { kind: "stamp-svc-unreachable"; reason: string }
  | { kind: "bundle-malformed"; reason: string };

export function render(state: UiState): HTMLElement {
  const root = document.createElement("div");
  root.className = "view";
  switch (state.kind) {
    case "loading":
      root.appendChild(masthead());
      root.appendChild(textEl("h1", "Verifying…", "loading-msg"));
      break;
    case "verify-result":
    case "offline-result":
      root.appendChild(masthead());
      renderResult(root, state.result, state.kind === "offline-result", state.download);
      root.appendChild(footer());
      break;
    case "offline-empty":
      root.appendChild(masthead());
      renderOfflineDropzone(root);
      root.appendChild(footer());
      break;
    case "stamp-not-found":
      root.classList.add("error");
      root.appendChild(masthead());
      root.appendChild(textEl("h1", "No such stamp"));
      root.appendChild(textEl("p", "We couldn't find an attestation with that identifier."));
      root.appendChild(footer());
      break;
    case "stamp-svc-unreachable": {
      root.classList.add("error");
      root.appendChild(masthead());
      root.appendChild(textEl("h1", "Stamp service unreachable"));
      root.appendChild(textEl("p", state.reason));
      root.appendChild(textEl("p", "If you have a saved bundle.json, you can verify it offline:"));
      const link = document.createElement("a");
      link.href = "/v/offline";
      link.textContent = "Open /v/offline";
      const wrap = document.createElement("p");
      wrap.appendChild(link);
      root.appendChild(wrap);
      root.appendChild(footer());
      break;
    }
    case "bundle-malformed":
      root.classList.add("error");
      root.appendChild(masthead());
      root.appendChild(textEl("h1", "Bundle malformed"));
      root.appendChild(textEl("p", state.reason));
      root.appendChild(footer());
      break;
  }
  return root;
}

// --------------- shared bits ---------------

function textEl(tag: string, text: string, className?: string): HTMLElement {
  const el = document.createElement(tag);
  el.textContent = text;
  if (className) el.className = className;
  return el;
}

function masthead(): HTMLElement {
  const m = document.createElement("div");
  m.className = "masthead";
  const a = document.createElement("a");
  a.href = "https://sigil.bot/";
  a.innerHTML = 'sigil<span class="dot">.</span>bot';
  m.appendChild(a);
  return m;
}

function footer(): HTMLElement {
  const f = document.createElement("footer");
  f.innerHTML =
    '&copy; 2026 <a href="https://sigil.bot/">sigil.bot</a> · ' +
    "attestation verifier · " +
    '<a href="/v/offline">offline</a>';
  return f;
}

// --------------- the seal (SVG) ---------------

function sealSvg(verified: boolean): SVGSVGElement {
  const svgNS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(svgNS, "svg");
  svg.setAttribute("class", `seal${verified ? "" : " seal--broken"}`);
  svg.setAttribute("viewBox", "0 0 240 240");
  svg.setAttribute("aria-label", verified ? "Verified seal" : "Broken seal");
  svg.setAttribute("role", "img");

  // ring path for textPath
  const ringPath = document.createElementNS(svgNS, "path");
  ringPath.setAttribute("id", "ring-text-path");
  ringPath.setAttribute("d", "M 120,120 m -86,0 a 86,86 0 1,1 172,0 a 86,86 0 1,1 -172,0");
  ringPath.setAttribute("fill", "none");
  const defs = document.createElementNS(svgNS, "defs");
  defs.appendChild(ringPath);
  svg.appendChild(defs);

  const fill = verified ? "#b82223" : "#5a5a55";
  const fillDeep = verified ? "#8a1a1b" : "#3a3835";
  const fg = "#fff";

  // outer rim
  const outerRim = document.createElementNS(svgNS, "circle");
  outerRim.setAttribute("cx", "120");
  outerRim.setAttribute("cy", "120");
  outerRim.setAttribute("r", "112");
  outerRim.setAttribute("fill", "none");
  outerRim.setAttribute("stroke", fillDeep);
  outerRim.setAttribute("stroke-width", "2");
  svg.appendChild(outerRim);

  // medallion
  const medallion = document.createElementNS(svgNS, "circle");
  medallion.setAttribute("cx", "120");
  medallion.setAttribute("cy", "120");
  medallion.setAttribute("r", "104");
  medallion.setAttribute("fill", fill);
  svg.appendChild(medallion);

  // hairline rings inside
  for (const r of ["94", "70"]) {
    const ring = document.createElementNS(svgNS, "circle");
    ring.setAttribute("cx", "120");
    ring.setAttribute("cy", "120");
    ring.setAttribute("r", r);
    ring.setAttribute("fill", "none");
    ring.setAttribute("stroke", fg);
    ring.setAttribute("stroke-width", "0.6");
    ring.setAttribute("opacity", "0.45");
    svg.appendChild(ring);
  }

  // ring text
  const ringText = document.createElementNS(svgNS, "text");
  ringText.setAttribute("fill", fg);
  ringText.setAttribute("font-family", "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace");
  ringText.setAttribute("font-size", "10.5");
  ringText.setAttribute("letter-spacing", "3.5");
  ringText.setAttribute("font-weight", "600");
  const textPath = document.createElementNS(svgNS, "textPath");
  textPath.setAttribute("href", "#ring-text-path");
  textPath.setAttribute("startOffset", "0");
  const ringPhrase = verified
    ? " SIGIL · ATTESTED · SIGIL · ATTESTED · SIGIL · ATTESTED · SIGIL · ATTESTED · "
    : " UNVERIFIED · SEAL BROKEN · UNVERIFIED · SEAL BROKEN · ";
  textPath.textContent = ringPhrase;
  ringText.appendChild(textPath);
  svg.appendChild(ringText);

  // center mark: stylized "S" wordmark with a dot, echoing the masthead
  const centerS = document.createElementNS(svgNS, "text");
  centerS.setAttribute("x", "120");
  centerS.setAttribute("y", "138");
  centerS.setAttribute("text-anchor", "middle");
  centerS.setAttribute("font-family", "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace");
  centerS.setAttribute("font-size", "56");
  centerS.setAttribute("font-weight", "700");
  centerS.setAttribute("fill", fg);
  centerS.setAttribute("letter-spacing", "-2");
  centerS.textContent = "S";
  svg.appendChild(centerS);

  const centerDot = document.createElementNS(svgNS, "circle");
  centerDot.setAttribute("cx", "143");
  centerDot.setAttribute("cy", "132");
  centerDot.setAttribute("r", "3");
  centerDot.setAttribute("fill", "#fff");
  svg.appendChild(centerDot);

  // crack overlay for broken state
  if (!verified) {
    const crack = document.createElementNS(svgNS, "path");
    crack.setAttribute("d", "M 50,90 L 110,118 L 90,150 L 160,180 L 200,150");
    crack.setAttribute("fill", "none");
    crack.setAttribute("stroke", "#1a1a1a");
    crack.setAttribute("stroke-width", "2.5");
    crack.setAttribute("stroke-linecap", "round");
    crack.setAttribute("stroke-linejoin", "round");
    crack.setAttribute("opacity", "0.85");
    svg.appendChild(crack);
  }

  return svg;
}

// --------------- result view ---------------

function renderResult(
  root: HTMLElement,
  r: VerificationResult,
  offline: boolean,
  download?: DownloadInfo,
): void {
  const verified = r.signatureVerified;

  // Screen-reader-friendly banner; small visual emphasis. Tests assert this exists.
  const banner = document.createElement("div");
  banner.className = verified ? "banner banner-ok" : "banner banner-bad";
  banner.textContent = verified ? "Signature verified" : "Signature does not verify";
  root.appendChild(banner);

  // Seal hero
  const sealWrap = document.createElement("div");
  sealWrap.className = "seal-wrap";
  sealWrap.appendChild(sealSvg(verified));
  sealWrap.appendChild(textEl("div", verified ? "Sealed by Sigil" : "Seal broken", "seal-label"));

  const summary = document.createElement("div");
  summary.className = "seal-summary";
  if (verified) {
    summary.innerHTML = `This message was attested as written by <strong>${escapeHtml(r.envelope.sender.display_name)}</strong> on <strong>${escapeHtml(formatFriendlyDate(r.signature.signed_at))}</strong>.`;
  } else {
    summary.innerHTML =
      "We could not verify this attestation. " +
      "The text may have been altered after stamping, or the receipt is forged. " +
      "Anything below should be treated as unverified.";
  }
  sealWrap.appendChild(summary);
  root.appendChild(sealWrap);

  // The attested content. ".content" preserved for test/tooling selectors.
  const messageEl = document.createElement("div");
  messageEl.className = "attested-message content";
  messageEl.textContent = r.envelope.content.text;
  root.appendChild(messageEl);

  // Attribution line
  const attribution = document.createElement("div");
  attribution.className = `attribution${verified ? "" : " attribution-bad"}`;
  const phone = r.envelope.sender.phone_e164;
  const claim = r.envelope.claim_type.replace(/_/g, " ");
  const lic = r.envelope.license;
  const tag = verified ? "" : " [unverified]";
  attribution.innerHTML =
    `<strong>${escapeHtml(r.envelope.sender.display_name)}</strong> ` +
    `<span class="sep">·</span> ${escapeHtml(phone)} ` +
    `<span class="sep">·</span> ${escapeHtml(claim)} ` +
    `<span class="sep">·</span> ${escapeHtml(lic)}${tag}`;
  root.appendChild(attribution);

  // Explanation cards
  root.appendChild(renderCards(r));

  // Download .ots
  if ((r.otsState === "submitted" || r.otsState === "confirmed") && download) {
    if (download.proofUrl) {
      const a = document.createElement("a");
      a.href = download.proofUrl;
      a.textContent = "Download .ots receipt";
      a.className = "download-ots";
      a.download = `${r.stampId}.ots`;
      root.appendChild(a);
    } else if (download.proofBase64) {
      const proofBase64 = download.proofBase64;
      const button = document.createElement("button");
      button.textContent = "Download .ots receipt";
      button.className = "download-ots";
      button.addEventListener("click", () => {
        const bytes = Uint8Array.from(atob(proofBase64), (c) => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${r.stampId}.ots`;
        a.click();
        URL.revokeObjectURL(url);
      });
      root.appendChild(button);
    }
  }

  // Offline indicator
  if (offline) {
    root.appendChild(
      textEl("p", "Verified entirely offline from your bundle.json.", "attribution"),
    );
  }

  // Technical detail (collapsed)
  root.appendChild(renderTechnical(r));
}

// --------------- explanation cards ---------------

function renderCards(r: VerificationResult): HTMLElement {
  const cards = document.createElement("div");
  cards.className = "cards";

  const sealedDate = formatFriendlyDate(r.signature.signed_at);

  // Card 1: what this proves
  const c1 = document.createElement("section");
  c1.className = "card";
  c1.appendChild(textEl("h3", "What the seal proves"));
  if (r.signatureVerified) {
    c1.appendChild(
      textEl(
        "p",
        `This exact text was signed by Sigil on behalf of ${r.envelope.sender.display_name} on ${sealedDate}. The signature seals both authorship and time. Any edit to the text after stamping would invalidate the seal.`,
      ),
    );
  } else {
    c1.appendChild(
      textEl(
        "p",
        "The signature does not verify. This means either the text was changed after stamping, the receipt is forged, or the signing key shown does not match Sigil's published key.",
      ),
    );
  }
  cards.appendChild(c1);

  // Card 2: how we verified
  const c2 = document.createElement("section");
  c2.className = "card";
  c2.appendChild(textEl("h3", "How we verified it"));
  c2.appendChild(
    textEl(
      "p",
      "Your browser checked the cryptographic signature against Sigil's published public key. No server was contacted to make this check. The proof is independent and works offline.",
    ),
  );
  cards.appendChild(c2);

  // Card 3: Bitcoin anchor
  const c3 = document.createElement("section");
  c3.className = "card";
  c3.appendChild(textEl("h3", "Bitcoin anchoring"));
  c3.appendChild(
    textEl(
      "p",
      "For long-term durability, every Sigil seal is also recorded on the Bitcoin blockchain via OpenTimestamps. Once recorded, anyone can independently confirm the timestamp, even if Sigil ceases to exist.",
    ),
  );

  const stateBadge = document.createElement("span");
  stateBadge.className = "state";
  if (r.otsState === "confirmed" && r.bitcoinAnchor) {
    stateBadge.classList.add("state--ok");
    stateBadge.innerHTML =
      `<span class="glyph">●</span> Anchored to Bitcoin block ${r.bitcoinAnchor.blockHeight} ` +
      `(${r.bitcoinAnchor.confirmations} confirmations) on ${formatFriendlyDate(r.bitcoinAnchor.blockTimestamp)}`;
  } else if (r.otsState === "confirmed") {
    stateBadge.classList.add("state--ok");
    stateBadge.innerHTML =
      '<span class="glyph">●</span> Anchored to Bitcoin (live confirmation lookup unavailable; the .ots receipt verifies against any Bitcoin node)';
  } else if (r.otsState === "submitted") {
    stateBadge.classList.add("state--pending");
    stateBadge.innerHTML =
      '<span class="glyph">○</span> Awaiting Bitcoin confirmation. Typically hours, but can take days when Bitcoin fees are high.';
  } else if (r.otsState === "pending") {
    stateBadge.classList.add("state--pending");
    stateBadge.innerHTML =
      '<span class="glyph">○</span> Submission to OpenTimestamps calendars in progress.';
  }
  c3.appendChild(stateBadge);
  cards.appendChild(c3);

  return cards;
}

// --------------- technical detail (collapsed) ---------------

function renderTechnical(r: VerificationResult): HTMLElement {
  const details = document.createElement("details");
  details.className = "technical";
  const summary = document.createElement("summary");
  summary.textContent = "Technical detail";
  details.appendChild(summary);

  const body = document.createElement("div");
  body.className = "technical-body";

  // Verification steps
  body.appendChild(
    textEl("p", "Each step below was checked in your browser before this page was rendered:"),
  );
  const steps = document.createElement("dl");
  steps.className = "steps";
  const stepLabels: Record<string, string> = {
    schema: "Schema",
    stampId: "Stamp ID",
    signature: "Signature",
    bitcoin: "Bitcoin",
  };
  for (const [name, step] of Object.entries(r.steps)) {
    const dt = document.createElement("dt");
    dt.textContent = stepLabels[name] ?? name;
    const dd = document.createElement("dd");
    dd.textContent = step.status + (step.message ? `: ${step.message}` : "");
    dd.dataset.status = step.status;
    steps.appendChild(dt);
    steps.appendChild(dd);
  }
  body.appendChild(steps);

  // Raw envelope identifiers
  const dl = document.createElement("dl");

  const rows: Array<[string, string]> = [
    ["Stamp ID", r.stampId],
    [
      "Issuer",
      `${r.envelope.issuer.service} · ${r.envelope.issuer.instance} · key ${r.envelope.issuer.key_id}`,
    ],
    ["Stamped at", r.envelope.stamped_at],
    ["Signed at", r.signature.signed_at],
    ["Content SHA-256", r.envelope.content.sha256],
    ["Envelope SHA-256", r.signature.envelope_sha256],
    ["Signature alg", r.signature.alg],
    ["Sigil public key", r.publicKeyB64],
  ];
  if (r.bitcoinAnchor) {
    rows.push([
      "BTC anchor",
      `block ${r.bitcoinAnchor.blockHeight} (${r.bitcoinAnchor.confirmations} conf) at ${r.bitcoinAnchor.blockTimestamp}`,
    ]);
  }

  for (const [label, value] of rows) {
    const row = document.createElement("div");
    row.className = "tech-row";
    const dt = document.createElement("dt");
    dt.textContent = label;
    const dd = document.createElement("dd");
    dd.textContent = value;
    row.appendChild(dt);
    row.appendChild(dd);
    dl.appendChild(row);
  }
  body.appendChild(dl);
  details.appendChild(body);

  return details;
}

// --------------- offline ---------------

function renderOfflineDropzone(root: HTMLElement): void {
  root.appendChild(textEl("h1", "Offline verification", "loading-msg"));
  root.appendChild(
    textEl(
      "p",
      "Drop a bundle.json (downloaded from a /v/<stamp_id> page) to verify it entirely in your browser.",
    ),
  );
  const dz = document.createElement("div");
  dz.id = "dropzone";
  dz.textContent = "Drop bundle.json here, or click to choose";
  root.appendChild(dz);
  const input = document.createElement("input");
  input.type = "file";
  input.id = "bundle-file";
  input.accept = ".json,application/json";
  root.appendChild(input);
}

// --------------- helpers ---------------

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatFriendlyDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}
